import { getAuthConfig } from "./environment";
import { getGatewayBaseUrl } from "./gateway";
import { authFetchCredentials, setCodeVerifier, getCodeVerifier, clearCodeVerifier } from "./authStorage";

const DEFAULT_REALM = "9s-realm";
const DEFAULT_CLIENT_ID = "9s-client";

function getIdpBaseUrl(): string {
  return (getAuthConfig().keycloak?.url || "").replace(/\/$/, "");
}

function base64UrlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(length: number): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  let result = "";
  const random = new Uint8Array(length);
  crypto.getRandomValues(random);
  for (let i = 0; i < length; i++) {
    result += chars[random[i] % chars.length];
  }
  return result;
}

export async function buildKeycloakAuthUrl(redirectUri: string): Promise<{ url: string; codeVerifier: string }> {
  const base = getIdpBaseUrl();
  const { keycloak } = getAuthConfig();
  const realm = keycloak?.realm || DEFAULT_REALM;
  const clientId = keycloak?.clientId || DEFAULT_CLIENT_ID;
  if (!base) {
    throw new Error("Keycloak URL not configured");
  }
  const codeVerifier = randomString(64);
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  const codeChallenge = base64UrlEncode(hash);
  setCodeVerifier(codeVerifier);
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid",
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  const url = `${base}/realms/${realm}/protocol/openid-connect/auth?${params.toString()}`;
  return { url, codeVerifier };
}

export async function exchangeCodeForToken(code: string, redirectUri: string): Promise<string> {
  const codeVerifier = getCodeVerifier();
  clearCodeVerifier();
  if (!codeVerifier) {
    throw new Error("Missing Keycloak config or code_verifier");
  }
  const res = await fetch(`${getGatewayBaseUrl()}/v1/auth/token`, {
    method: "POST",
    credentials: authFetchCredentials(),
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as { data?: { access_token?: string } };
  const accessToken = data.data?.access_token;
  if (!accessToken) {
    throw new Error("No access_token in response");
  }
  return accessToken;
}

export function decodePayloadRole(token: string): string {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return "member";
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    const roles = payload?.realm_access?.roles;
    if (Array.isArray(roles) && roles.includes("admin")) return "admin";
    if (Array.isArray(roles) && roles.length > 0) return roles[0];
    return "member";
  } catch {
    return "member";
  }
}
